import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconButton, Typography, useClickOutside } from "@digital-services/ui";

import { shortenOrgName } from "@organization/utils";

import { useOrganizationContext } from "@hooks/useOrganizationContext";
import DownIcon from "@icons/down_16.svg?react";

import AccountMenuContent from "./AccountMenuContent";

const AccountMenu = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "organization" });

  const [expanded, setExpanded] = useState(false);
  const [shouldRenderChildren, setShouldRenderChildren] = useState(false);

  const { organization: currentOrganization } = useOrganizationContext();

  // Remove this line after upgrade to `@digital-services/ui` v2.0.2 exactly or any latest.
  // eslint-disable-next-line
  const menuRef = useRef<any>(null);

  useClickOutside(menuRef, () => {
    handleCollapse();
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (expanded === false) {
      timer = setTimeout(() => setShouldRenderChildren(false), 400);
    } else {
      setShouldRenderChildren(true);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [expanded]);

  const handleExpand = () => {
    setExpanded(true);
  };

  const handleCollapse = () => {
    setExpanded(false);
  };

  const isCardExpanded = () => {
    return expanded;
  };

  const cardContent = (
    <>
      <div data-testid="current-organization" className="w-full text-right">
        <Typography>
          <span>{t("currentOrganization.label")}</span>&nbsp;
          <span className="font-bold text-gray-700">
            {shortenOrgName(currentOrganization.name)}
          </span>
        </Typography>
      </div>
      <div className="ml-0">
        <IconButton>
          <DownIcon
            data-testid="user-profile-toggle-icon"
            className={isCardExpanded() ? "rotate-180" : ""}
          />
        </IconButton>
      </div>
    </>
  );

  const expandableCardContent = <AccountMenuContent />;

  let cardContentWrapperCssClasses = isCardExpanded()
    ? "grid-rows-[1fr] opacity-100"
    : "grid-rows-[0fr] opacity-0";

  cardContentWrapperCssClasses +=
    " duration-400 mt-1 grid w-full transition-all ease-in";

  return (
    <div
      className="relative max-h-full"
      data-testid="account-menu"
      ref={menuRef}
    >
      <div
        className="flex items-center hover:cursor-pointer"
        onClick={isCardExpanded() ? handleCollapse : handleExpand}
      >
        {cardContent}
      </div>
      <div className={cardContentWrapperCssClasses}>
        <div className="absolute right-0 -mx-4 -mb-4 min-w-max overflow-hidden px-4 pb-4">
          {shouldRenderChildren && expandableCardContent}
        </div>
      </div>
    </div>
  );
};

export default AccountMenu;
